import React from "react";
import { Box } from "@mui/joy";
import DocumentTypeComponent from "./display/DocumentTypeComponent";
import { useCustomer, useTraining } from "../../contexts/api";
import { useAlert } from "../../contexts/app/notificationToast";
import type { Training } from "../../api/training/Training.typed";
import type { ZekayCustomer } from "../../api/customers/Customer.typed";
import { SFile } from "../../styles/StyledMUI";
import SheetTypeComponent from "./display/SheetTypeComponent";
import { OpenPdfInNewTab } from "../../utils/pdfViewer";

interface DocumentsComponentProps {
  training: Training;
  customer?: ZekayCustomer;
  standalone?: boolean;
  onCustomerUpdated?: (customer: ZekayCustomer) => void;
  setTrainings?: React.Dispatch<React.SetStateAction<Training[] | undefined>>;
  refreshTrainings?: () => Promise<Training[] | undefined>;
}

export type DocumentsBadgeStatus = {
  uploaded: number;
  total: number;
  allUploaded: boolean;
};

// Counts required documents (medical cert, +SDI pair) and how many have a URL.
export const getDocumentsBadgeStatus = (
  training: Training,
  customer?: ZekayCustomer,
): DocumentsBadgeStatus => {
  const hasUrl = (value: unknown): boolean => typeof value === "string" && value.trim() !== "";
  const docs: boolean[] = [hasUrl(customer?.MedicalCertificate)];
  if (training.IsSDI) {
    docs.push(hasUrl(training.MedicalDisclaimer));
    docs.push(hasUrl(training.MedicalAgreement));
  }
  const total = docs.length;
  const uploaded = docs.filter(Boolean).length;
  return { uploaded, total, allUploaded: total > 0 && uploaded === total };
};

export default function DocumentsComponent(props: DocumentsComponentProps) {
  const {
    training,
    customer: customerProp,
    standalone,
    onCustomerUpdated,
    setTrainings,
    refreshTrainings,
  } = props;
  const { updateFile, deleteCustomerFile, getCustomer } = useCustomer();
  const { updateTrainingFile, deleteTrainingFile } = useTraining();
  const [customer, setCustomer] = React.useState<ZekayCustomer | undefined>(customerProp);
  const { showAlert } = useAlert();

  const doRefresh = React.useCallback(async (): Promise<Training[] | undefined> => {
    if (!refreshTrainings) return undefined;
    const data = await refreshTrainings();
    const list = Array.isArray(data) ? data : undefined;
    if (list && setTrainings) setTrainings(list);
    return list;
  }, [refreshTrainings, setTrainings]);

  React.useEffect(() => {
    if (customerProp?.Id === training?.CustomerID) {
      setCustomer(customerProp);
      return;
    }
    if (!training?.CustomerID) return;
    let isMounted = true;
    getCustomer(String(training.CustomerID)).then((c) => {
      if (isMounted) setCustomer(c);
    });
    return () => {
      isMounted = false;
    };
  }, [training?.CustomerID, customerProp, getCustomer]);

  const uploadFile = React.useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>, type: string) => {
      const normalizedType = (type || "").trim().toLowerCase();
      const file = event.target.files?.[0];
      if (!file) {
        showAlert("Aucun fichier sélectionné", "warning");
        return;
      }

      try {
        if (normalizedType === "medical") {
          if (!customer?.Id) {
            showAlert("Client introuvable pour l’upload du certificat", "danger");
            return;
          }

          const formData = new FormData();
          formData.append("CustomerID", String(customer.Id));
          formData.append("MedicalCertificate", file);

          await updateFile(formData, customer.Id);

          // Briefly wait for the file to be available on the server
          await new Promise((resolve) => setTimeout(resolve, 1000));

          const refreshed = await getCustomer(String(customer.Id));
          setCustomer(refreshed);
          if (refreshed) onCustomerUpdated?.(refreshed);
          if (!onCustomerUpdated) await doRefresh();
          return;
        }

        // Default flow: training files uploaded via FormData (API: POST /trainings/{id}/file, body: Type + File only)
        const formData = new FormData();
        formData.append("Type", normalizedType);
        formData.append("File", file);

        const trainingId = training.ID;
        if (trainingId == null) {
          showAlert("Formation sans ID : impossible d'uploader le fichier", "danger");
          return;
        }
        await updateTrainingFile(Number(trainingId), formData);
        await new Promise((resolve) => setTimeout(resolve, 1000));
        await doRefresh();
      } catch (err) {
        if (err instanceof Error) {
          showAlert(err.message, "danger");
        } else {
          showAlert("Erreur lors de l’upload du fichier", "danger");
        }
      }
    },
    [
      training,
      customer,
      doRefresh,
      onCustomerUpdated,
      showAlert,
      updateTrainingFile,
      updateFile,
      getCustomer,
    ],
  );

  const deleteFile = React.useCallback(
    async (type: string) => {
      const normalizedType = (type || "").trim().toLowerCase();
      if (normalizedType === "medical") {
        if (!customer?.Id) {
          showAlert("Client introuvable pour suppression", "danger");
          return;
        }
        await deleteCustomerFile({ CustomerID: customer.Id, Type: "medical" });
        // Briefly wait for the backend to finalize deletion
        await new Promise((resolve) => setTimeout(resolve, 500));
        const refreshed = await getCustomer(String(customer.Id));
        setCustomer(refreshed);
        if (refreshed) onCustomerUpdated?.(refreshed);
        if (!onCustomerUpdated) await doRefresh();
        return;
      }
      const trainingId = training.ID;
      if (trainingId == null) {
        showAlert("Formation sans ID : impossible de supprimer le fichier", "danger");
        return;
      }
      await deleteTrainingFile(Number(trainingId), normalizedType);
      await new Promise((resolve) => setTimeout(resolve, 500));
      await doRefresh();
    },
    [
      training,
      customer,
      doRefresh,
      onCustomerUpdated,
      showAlert,
      deleteTrainingFile,
      deleteCustomerFile,
      getCustomer,
    ],
  );

  const handleView = React.useCallback(
    (type: string) => {
      let fileToOpen: string | undefined;
      switch (type) {
        case "sheet":
          fileToOpen = training?.TrainingSheet;
          break;
        case "agreement":
          fileToOpen = training?.MedicalAgreement;
          break;
        case "disclaimer":
          fileToOpen = training?.MedicalDisclaimer;
          break;
        case "medical":
          {
            const mc = customer?.MedicalCertificate;
            fileToOpen = typeof mc === "string" ? mc : undefined;
          }
          break;
      }
      if (!fileToOpen) {
        showAlert("Aucun document à afficher", "warning");
        return;
      }
      OpenPdfInNewTab(fileToOpen);
    },
    [
      training?.TrainingSheet,
      training?.MedicalAgreement,
      training?.MedicalDisclaimer,
      customer?.MedicalCertificate,
      showAlert,
    ],
  );

  const content = (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        marginTop: 1,
        marginBottom: 1,
      }}
    >
      <SFile />
      <Box
        sx={{
          marginLeft: { xs: 0, sm: "10px" },
          display: "flex",
          gap: { xs: 1, sm: 2 },
          flexWrap: { xs: "wrap", sm: "nowrap" },
        }}
      >
        <SheetTypeComponent
          document={training?.TrainingSheet}
          uploadFile={uploadFile}
          deleteFile={deleteFile}
          handleView={handleView}
          documentType="sheet"
          training={training}
          refreshTrainings={doRefresh}
          setTrainings={setTrainings!}
        />
        {training.IsSDI && (
          <>
            <DocumentTypeComponent
              document={training?.MedicalDisclaimer}
              uploadFile={uploadFile}
              deleteFile={deleteFile}
              handleView={handleView}
              title="Décharge de responsabilité"
              documentType="disclaimer"
            />
            <DocumentTypeComponent
              document={training?.MedicalAgreement}
              uploadFile={uploadFile}
              deleteFile={deleteFile}
              handleView={handleView}
              title="Décharge médicale"
              documentType="agreement"
            />
          </>
        )}
        <DocumentTypeComponent
          document={
            typeof customer?.MedicalCertificate === "string"
              ? customer?.MedicalCertificate
              : undefined
          }
          uploadFile={uploadFile}
          deleteFile={deleteFile}
          handleView={handleView}
          title="Certificat médical"
          documentType="medical"
        />
      </Box>
    </Box>
  );

  if (standalone) {
    return <Box sx={{ py: 1 }}>{content}</Box>;
  }
  return (
    <tr>
      <td colSpan={12} style={{ border: "none" }}>
        {content}
      </td>
    </tr>
  );
}
